import { useEffect, useMemo } from "react";
import Spacer from "../../../common/components/Spacer";
import { Col, Row } from "../../../common/components/Layout";
import CheckoutSummary from "./CheckoutSummary/CheckoutSummary";
import { Permission, userHasPermission } from "../../../utils/permissions";
import {
  selectCheckoutCart,
  selectDeliveryModeDetails,
  selectIsSending,
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "../../redux/selectors/checkoutPageSelectors";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "../../../common/components/icon/Icon";
import { IconType } from "../../../common/components/icon/IconType";
import { userIsInquirer, userIsInternalCustomer } from "../../../utils/roles";
import { type RootState } from "../../../redux/store";
import { PackageEntrySource } from "../../models/CartData";
import Modal from "../../../common/components/Modal";
import { useBooleanState } from "../../../common/hooks/useBooleanState";
import CheckoutSectionTitle from "./CheckoutSectionTitle";
import ProductTable from "../CartPage/ProductTable/ProductTable";
import OrderTotals from "../CartPage/OrderTotals";
import { CheckoutFormSaver, useCheckoutLogic } from "../../useCheckoutLogic";
import { FormProvider } from "react-hook-form";
import { selectPackageEntryMap } from "../../redux/selectors/cartSelectors";
import { type DeliveryOptionItem } from "./DeliveryOption";
import { LoadingButton } from "../../../common/components/Button/Button";
import ToastMessage from "../../../common/components/Messages/ToastMessage/ToastMessage";
import { trackCheckoutLoadThunk } from "../../redux/actions/checkoutPageActions";
import { useTranslation } from "react-i18next";
import { openModal } from "~/AddToCart/ModalSevice";
import { CantQuoteSalesCampaignModal } from "~/SalesCampaign/dialogs/CantQuoteSalesCampaignModal/CantQuoteSalesCampaignModal";
import { ErrorCode } from "~/Cart/api/placeOrder";
import { SoldOutCampaignModal } from "~/SalesCampaign/dialogs/SoldOutCampaignModal/SoldOutCampaignModal";

import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartSurcharge,
} from "~/Cart/redux/selectors/selectors";
import { Stack, Typography } from "@mui/material";

export interface CheckoutForm {
  poNumber: string;
  orderComment: string;
  deliveryCode: string;
  completeDelivery: DeliveryOptionItem;

  optime?: CheckoutFormOptime;
  paymentType: string;
  creditCardID: string;
  freightPaymentCode: string;
  freightPaymentAccountNumber: string;
  orderType: string;
}

export interface CheckoutFormOptime {
  consent: boolean;
  email: string;
  name: string;
  lastName: string;
  plant: string;
  timeZone: string;
  language: string;
  country: string;
}

export interface FreightPaymentItem {
  type: string;
  accountNumber: boolean;
}

const TOAST_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.Generic]: "checkout.placeOrder.failed",
  [ErrorCode.CreditCard]: "checkout.placeOrder.payment.failed",
  [ErrorCode.SaleStock]: "",
};

const CheckoutCart = () => {
  const { t } = useTranslation();

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const { any: hasSomeSalesCampaignItems, all: hasOnlySalesCampaignItems } = useSelector(
    selectContainsSalesCampaignItems,
  );

  const cartData = useSelector(selectCheckoutCart);

  const containsQuoteItemsInQuotables = useSelector(selectContainsQuoteInQuotables);
  const containsQuoteItemsAtAll = useSelector(selectContainsQuoteItemsAtAll);

  const { handleSubmitClick, formProps, placeOrderError, resetPlaceOrderError } = useCheckoutLogic();

  const { watch, setValue } = formProps;

  const deliveryCode = watch("deliveryCode");
  const methodPayment = watch("paymentType");
  const orderTypeState = watch("orderType");
  const deliveryDetails = useSelector(selectDeliveryModeDetails(deliveryCode, methodPayment));
  const expressDelivery = "EXPRESS";

  let extraElements = undefined;
  if (deliveryDetails && deliveryDetails?.paymentCodeDisplay) {
    const shipmentMethodLabel = t(`data.shipmentMethod.${deliveryDetails.code}`);

    extraElements = [
      {
        label: t("checkout.shipmentInfo.label", {
          context: window.SCHA.config.translationCountryCode,
          shipmentType: shipmentMethodLabel,
        }),
        value:
          deliveryDetails.code === expressDelivery
            ? t(`data.paymentCodeDisplay.EXPRESS`, {
                context: window.SCHA.config.translationCountryCode,
              })
            : undefined,
      },
    ];
  }

  const totalWeight = useSelector(selectTotalWeight);
  const totalSum = useSelector(selectTotalCheckoutValue);

  const hasCartElement =
    cartData.orderables.entries.length > 0 || cartData.bundles.some((bundle) => bundle.entries.length > 0);
  const hasQuoteElement = cartData.quotables.entries.length > 0;

  const displayOrderableItemsSection = cartData.orderables.entries.length > 0;

  const sending = useSelector(selectIsSending);

  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);

  const surchargeValue = useSelector(selectCartSurcharge);

  const {
    val: showQuoteItemsFromQuoteInfo,
    setTrue: openShowQuoteItemsFromQuoteInfo,
    setFalse: closeShowQuoteItemsFromQuoteInfo,
  } = useBooleanState(false);

  const dispatch = useDispatch();

  const disablePlaceQuote =
    webservicesDisabled || !userHasPermission(Permission.PLACE_QUOTE) || hasOnlySalesCampaignItems;
  const disablePlaceOrder =
    webservicesDisabled || !userHasPermission(Permission.PLACE_ORDER) || userIsInquirer() || userIsInternalCustomer();

  const bothButtonsDisabled = disablePlaceOrder && disablePlaceQuote;

  const actionButtons = useMemo(() => {
    const mixedCart = hasCartElement && hasQuoteElement;

    const actions = [];

    if (hasCartElement) {
      actions.push(
        <Stack gap={2}>
          <LoadingButton
            key={"placeOrder"}
            data-meta={"checkout"}
            data-test-id={hasQuoteElement ? "btn-place-mixed" : "btn-place-order"}
            className={"f-no-wrap"}
            fullWidth={true}
            size="large"
            disabled={disablePlaceOrder}
            inFlight={sending}
            onClickCapture={() => {
              const paymentType = formProps.getValues("paymentType");
              dispatch(trackCheckoutLoadThunk(paymentType));
            }}
            onClick={() => {
              if (containsQuoteItemsInQuotables) {
                openShowQuoteItemsFromQuoteInfo();
              } else {
                handleSubmitClick(false);
              }
            }}
          >
            {hasQuoteElement ? t("page.checkout.btn.mixedOrder") : t("checkoutPage.btn.order")}
          </LoadingButton>
          {mixedCart && <Typography>{t("page.checkout.btnDescription.order.mixedOrderAndQuote")}</Typography>}
        </Stack>,
      );
    }

    actions.push(
      <div>
        <LoadingButton
          key={"placeQuote"}
          data-meta={"checkout"}
          data-test-id={"btn-place-quote"}
          color={hasCartElement ? "secondary" : "primary"}
          className={"f-no-wrap"}
          fullWidth={true}
          size="large"
          disabled={disablePlaceQuote}
          inFlight={sending}
          onClickCapture={() => {
            const paymentType = formProps.getValues("paymentType");
            dispatch(trackCheckoutLoadThunk(paymentType));
          }}
          onClick={() => {
            if (containsQuoteItemsAtAll) {
              openShowQuoteItemsFromQuoteInfo();
            } else if (hasSomeSalesCampaignItems) {
              openModal("CantQuoteSalesCampaignModal", (onClose) => (
                <CantQuoteSalesCampaignModal
                  onClose={onClose}
                  disablePlaceOrder={disablePlaceOrder}
                  onPlaceOrder={() => handleSubmitClick(false)}
                />
              ));
            } else {
              handleSubmitClick(true);
            }
          }}
        >
          {mixedCart ? t("page.checkout.btnDescription.quote") : t("checkoutPage.btn.quote")}
        </LoadingButton>
      </div>,
    );

    return actions;
  }, [sending, t, handleSubmitClick, hasQuoteElement, hasCartElement]);

  const cartProductsList = [
    ...cartData.orderables.entries,
    ...cartData.quotables.entries,
    ...cartData.bundles.flatMap((bundle) => bundle.entries),
  ];

  const packageEntryMap = useSelector(selectPackageEntryMap);

  // create a list of all available products in checkout
  // extract for each product user's delivery date
  const cartProductsDeliveryDateList: Date[] = cartProductsList
    .flatMap((groupingEntry) => groupingEntry.packGroups)
    .flatMap((packGroup) => packGroup.packEntryIds)
    .map((packId) => packageEntryMap[packId])
    .filter((packData) => packData.edd && packData.edd.date !== null && packData.edd.date !== undefined)
    .map((packData) => packData.edd?.date) as Date[];

  const selectedOrderType = window.SCHA.config.selectedOrderType;

  useEffect(() => {
    if (!orderTypeState && !!selectedOrderType) {
      setValue("orderType", selectedOrderType);
    }
  }, []);

  const showToast = placeOrderError && [ErrorCode.Generic, ErrorCode.CreditCard].includes(placeOrderError);

  useEffect(() => {
    if (placeOrderError === ErrorCode.SaleStock) {
      openModal("OutOfStockModal", (onClose) => (
        <SoldOutCampaignModal
          onClose={() => {
            onClose();
            resetPlaceOrderError();
          }}
        />
      ));
    }
  }, [placeOrderError]);

  return (
    <>
      {showToast && <ToastMessage type={"error"}>{t(TOAST_MESSAGES[placeOrderError])}</ToastMessage>}
      <FormProvider {...formProps}>
        <CheckoutFormSaver />
        <CheckoutSummary
          actionButtons={actionButtons}
          cartProductsDeliveryDateList={cartProductsDeliveryDateList}
          surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
          tariffSurcharge={hasAnyTariffSurcharge}
          bothButtonsDisabled={bothButtonsDisabled}
        />

        {displayOrderableItemsSection && (
          <>
            <CheckoutSectionTitle>
              {t("cartPage.header.toBeDelivered", { count: cartData.orderables.entries.length })}
            </CheckoutSectionTitle>
            <ProductTable data-test-id={"checkout-section-orderable"} group={cartData.orderables} editMode={false} />
          </>
        )}

        {cartData.bundles.map((bundle) => (
          <>
            <Spacer isThick={true} className={"mt-30 mb-60"} />
            <CheckoutSectionTitle>
              {t("page.cart.bundledQuote.title", { amount: bundle.entries.length, quoteNumber: bundle.quoteNumber })}
            </CheckoutSectionTitle>
            <p className="mt-2 mb-3">{t("page.cart.bundledQuote.subtitle")}</p>
            <ProductTable data-test-id={`checkout-section-bundle-${bundle.quoteNumber}`} group={bundle} />
          </>
        ))}

        {hasQuoteElement && (
          <>
            <Spacer isThick={true} className={"mt-30 mb-60"} />
            <CheckoutSectionTitle>
              {t("cartPage.header.toBeQuoted", { count: cartData.quotables.entries.length })}
            </CheckoutSectionTitle>
            <ProductTable data-test-id={"checkout-section-quotable"} group={cartData.quotables} editMode={false} />
          </>
        )}

        <Spacer isThick={true} className={"mt-30 mb-30"} />

        <Row className={"mb-30"}>
          <Col size={8} />
          <Col size={4}>
            <OrderTotals
              showPriceOnRequest={true}
              weight={totalWeight}
              totalValue={webservicesDisabled ? null : totalSum}
              rows={extraElements}
              surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
              tariffSurcharge={hasAnyTariffSurcharge}
            >
              {actionButtons}
            </OrderTotals>
          </Col>
        </Row>

        {showQuoteItemsFromQuoteInfo && (
          <Modal onClose={closeShowQuoteItemsFromQuoteInfo}>{t("page.checkout.message.alreadyQuotedItems")}</Modal>
        )}
      </FormProvider>
    </>
  );
};

export default CheckoutCart;

const selectContainsQuoteInQuotables = (state: RootState) => {
  const quotables = selectCheckoutCart(state).quotables;

  if (quotables.entries.length === 0) {
    return false;
  }

  const packEntryMap = state.cart.packEntriesById;

  return quotables.entries
    .flatMap((entry) => entry.packGroups)
    .flatMap(($) => $.packEntryIds)
    .some((packEntryId) => packEntryMap[packEntryId].source === PackageEntrySource.QUOTE);
};

const selectContainsQuoteItemsAtAll = (state: RootState) => {
  return Object.values(state.cart.packEntriesById).some((packEntry) => packEntry.source === PackageEntrySource.QUOTE);
};

const selectContainsSalesCampaignItems = (state: RootState) => {
  let any = false;
  let all = true;
  Object.values(state.cart.packEntriesById).forEach((packEntry) => {
    any = any || !!packEntry.saleCampaignEnabled;
    all = all && !!packEntry.saleCampaignEnabled;
  });
  return {
    any,
    all,
  };
};
